import { createApi, fakeBaseQuery, type QueryReturnValue } from '@reduxjs/toolkit/query/react';
import type { OrderByDirection } from 'firebase/firestore';
import type { AppError } from '../../utils/firebaseErrors';
import {
  type UserAccountFilterItem,
  type CreateUserAccountArgs,
  type CreateUserAccountResult,
  type UpdateUserAccountArgs,
  type DeleteUserAccountArgs,
  type DisableUserAccountArgs,
  type EnableUserAccountArgs,
  type InitiateUserCredentialSetupArgs,
  type UserCredentialSetupResult,
  type InitiateUserCredentialResetArgs,
  type UserCredentialResetResult,
  type PaginatedUserAccounts,
  type UserAccountsBackendGateway,
} from './userAccountsGateway';
import { buildUserAccountsStaleReadKeys } from '../staleReadFallback/queryKeys';
import {
  recordSuccessfulRead,
  resolveStaleReadFallback,
  type StaleReadMeta,
} from '../staleReadFallback/staleReadCache';
import { isBackendGatewayError, toAppError } from '../../utils/backendGatewayError';
import type { AppDependencies } from '../../infrastructure/appDependencies';
import { buildCanonicalUserAccountListQueryIntent } from './userAccountQuery';

export type {
  UserAccountFilterItem,
  CreateUserAccountArgs,
  CreateUserAccountResult,
  UpdateUserAccountArgs,
  DeleteUserAccountArgs,
  DisableUserAccountArgs,
  EnableUserAccountArgs,
  InitiateUserCredentialSetupArgs,
  UserCredentialSetupResult,
  InitiateUserCredentialResetArgs,
  UserCredentialResetResult,
};

export type UserAccountsQuery = Parameters<UserAccountsBackendGateway['listUserAccounts']>[0];

type StaleReadable<T> = T & { staleRead?: StaleReadMeta };

export interface UserAccountsQueryState {
  filters: UserAccountFilterItem[];
  page: number;
  pageSize: number;
  sortField?: string | null;
  sortDir?: OrderByDirection;
}

export const adaptUserAccountsQuery = (params: UserAccountsQuery): UserAccountsQuery => ({
  ...buildCanonicalUserAccountListQueryIntent(params),
});

type ListUserAccountsResult = PaginatedUserAccounts;
type UserAccountsResult = StaleReadable<ListUserAccountsResult>;

const getAppDependencies = (extra: unknown): AppDependencies => {
  if (extra && typeof extra === 'object' && 'userAccountsBackend' in extra) {
    return extra as AppDependencies;
  }

  throw new Error('App Runtime dependencies are not configured for user accounts API');
};

const getUserAccountsBackend = (extra: unknown): UserAccountsBackendGateway =>
  getAppDependencies(extra).userAccountsBackend;

export const userAccountsApi = createApi({
  reducerPath: 'userAccountsApi',
  baseQuery: fakeBaseQuery<AppError>(),
  tagTypes: ['UserAccount'],
  keepUnusedDataFor: 60,
  endpoints: (builder) => ({
    getUserAccounts: builder.query<UserAccountsResult, UserAccountsQuery>({
      async queryFn(
        { filters = [], page, pageSize, sortField, sortDir },
        { extra }
      ): Promise<QueryReturnValue<UserAccountsResult, AppError, {}>> {
        const userAccountsBackend = getUserAccountsBackend(extra);
        const canonicalQuery = buildCanonicalUserAccountListQueryIntent({
          filters,
          page,
          pageSize,
          sortField: sortField ?? undefined,
          sortDir: sortDir ?? undefined,
        });
        const staleReadKeys = buildUserAccountsStaleReadKeys(canonicalQuery);

        try {
          const userAccounts = await userAccountsBackend.listUserAccounts(canonicalQuery);

          recordSuccessfulRead(staleReadKeys.exactKey, staleReadKeys.familyKey, userAccounts);

          return { data: userAccounts };
        } catch (error) {
          if (isInvalidQueryFailure(error)) {
            return { error: toAppError(error) };
          }

          const staleResult = resolveStaleReadFallback<ListUserAccountsResult>(
            staleReadKeys.exactKey,
            staleReadKeys.familyKey,
            true
          );

          if (staleResult) {
            return { data: addStaleReadMeta(staleResult.data, staleResult.staleRead) };
          }

          return { error: toAppError(error) };
        }
      },
      providesTags: (result) =>
        result
          ? [
              ...result.items.map(({ uid }) => ({ type: 'UserAccount' as const, id: uid })),
              'UserAccount',
            ]
          : ['UserAccount'],
    }),
    createUserAccount: builder.mutation<CreateUserAccountResult, CreateUserAccountArgs>({
      async queryFn(
        args,
        { extra }
      ): Promise<QueryReturnValue<CreateUserAccountResult, AppError, {}>> {
        const userAccountsBackend = getUserAccountsBackend(extra);
        try {
          const result = await userAccountsBackend.createUserAccount(args);
          return { data: result };
        } catch (error) {
          return { error: toAppError(error) };
        }
      },
      invalidatesTags: () => ['UserAccount'],
    }),
    initiateUserCredentialSetup: builder.mutation<
      UserCredentialSetupResult,
      InitiateUserCredentialSetupArgs
    >({
      async queryFn(
        args,
        { extra }
      ): Promise<QueryReturnValue<UserCredentialSetupResult, AppError, {}>> {
        const userAccountsBackend = getUserAccountsBackend(extra);
        try {
          const result = await userAccountsBackend.initiateUserCredentialSetup(args);
          return { data: result };
        } catch (error) {
          return { error: toAppError(error) };
        }
      },
    }),
    initiateUserCredentialReset: builder.mutation<
      UserCredentialResetResult,
      InitiateUserCredentialResetArgs
    >({
      async queryFn(
        args,
        { extra }
      ): Promise<QueryReturnValue<UserCredentialResetResult, AppError, {}>> {
        const userAccountsBackend = getUserAccountsBackend(extra);
        try {
          const result = await userAccountsBackend.initiateUserCredentialReset(args);
          return { data: result };
        } catch (error) {
          return { error: toAppError(error) };
        }
      },
    }),
    updateUserAccount: builder.mutation<void, UpdateUserAccountArgs>({
      async queryFn(args, { extra }): Promise<QueryReturnValue<void, AppError, {}>> {
        const userAccountsBackend = getUserAccountsBackend(extra);
        try {
          await userAccountsBackend.updateUserAccount(args);
          return { data: undefined };
        } catch (error) {
          return { error: toAppError(error) };
        }
      },
      invalidatesTags: (_result, _error, { uid }) => [{ type: 'UserAccount', id: uid }],
    }),
    disableUserAccount: builder.mutation<void, DisableUserAccountArgs>({
      async queryFn(args, { extra }): Promise<QueryReturnValue<void, AppError, {}>> {
        const userAccountsBackend = getUserAccountsBackend(extra);
        try {
          await userAccountsBackend.disableUserAccount(args);
          return { data: undefined };
        } catch (error) {
          return { error: toAppError(error) };
        }
      },
      invalidatesTags: (_result, _error, { uid }) => [{ type: 'UserAccount', id: uid }],
    }),
    enableUserAccount: builder.mutation<void, EnableUserAccountArgs>({
      async queryFn(args, { extra }): Promise<QueryReturnValue<void, AppError, {}>> {
        const userAccountsBackend = getUserAccountsBackend(extra);
        try {
          await userAccountsBackend.enableUserAccount(args);
          return { data: undefined };
        } catch (error) {
          return { error: toAppError(error) };
        }
      },
      invalidatesTags: (_result, _error, { uid }) => [{ type: 'UserAccount', id: uid }],
    }),
    deleteUserAccount: builder.mutation<void, DeleteUserAccountArgs>({
      async queryFn(args, { extra }): Promise<QueryReturnValue<void, AppError, {}>> {
        const userAccountsBackend = getUserAccountsBackend(extra);
        try {
          await userAccountsBackend.deleteUserAccount(args);
          return { data: undefined };
        } catch (error) {
          return { error: toAppError(error) };
        }
      },
      invalidatesTags: (_result, _error, { uid }) => [{ type: 'UserAccount', id: uid }],
    }),
  }),
});

const addStaleReadMeta = <T>(data: T, staleRead: StaleReadMeta): T => {
  if (Array.isArray(data)) {
    return Object.assign([...data], { staleRead }) as T;
  }

  if (data && typeof data === 'object') {
    return Object.assign({}, data, { staleRead }) as T;
  }

  return data;
};

const isInvalidQueryFailure = (error: unknown): boolean => {
  if (isBackendGatewayError(error)) {
    if (error.reason.includes('invalid-query')) {
      return true;
    }

    return isInvalidQueryFailure(error.original);
  }

  if (!error || typeof error !== 'object') {
    return false;
  }

  const record = error as { code?: unknown; reason?: unknown };
  const code = typeof record.code === 'string' ? record.code : '';
  const reason = typeof record.reason === 'string' ? record.reason : '';

  return (
    code.includes('invalid-argument') ||
    code.includes('invalid-query') ||
    reason.includes('invalid-query')
  );
};

export const {
  useGetUserAccountsQuery,
  useCreateUserAccountMutation,
  useInitiateUserCredentialSetupMutation,
  useInitiateUserCredentialResetMutation,
  useUpdateUserAccountMutation,
  useDisableUserAccountMutation,
  useEnableUserAccountMutation,
  useDeleteUserAccountMutation,
} = userAccountsApi;
